import React, { memo } from 'react'

import { string, bool, shape, arrayOf } from 'prop-types'

import { useActionsData } from 'Pods/UnifiedNavigation/Hooks/useActionsData'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import CartProgressBar from './Components/CartProgressBar/CartProgressBar'
import GenerationStatusAction from './Components/GenerationStatusAction/GenerationStatusAction'
import UserAction from './Components/UserAction/UserAction'
import UserMenu from './Components/UserMenu/UserMenu'
import { useCartWidget } from './Hooks/useCartWidget'
import { useMessagesWidget } from './Hooks/useMessagesWidget'
import { useNotificationsWidget } from './Hooks/useNotificationsWidget'

// Actions the redesigned bar surfaces; upload / boost-credits are no longer in the
// top navigation. Exported so the shared mobile backdrop can dim behind these
// popovers too (each keys its overlay state on its own id - see Navigation).
export const VISIBLE_ACTION_IDS = ['cart-action', 'inbox-action', 'notifications-action']

// Redesign uses the filled glyphs. Overrides the server-provided icon for the new nav actions.
const ICONS_MAP = {
  'cart-action': 'cart',
  'inbox-action': 'message',
  'notifications-action': 'notification',
}

function UserActions({
  actionsLinks = EMPTY_ARRAY,
  actionsConfig = EMPTY_OBJECT,
  locale = 'en',
  showFreeTrialTag = false,
  freeTrialLabel = '',
  createAiItem = EMPTY_OBJECT,
  createAiGeneration = EMPTY_OBJECT,
  currentUserId = null,
}) {
  const { isCurrentUser = false, newRoyaltyUi = false, showCartAds = false } = actionsConfig
  const { cart, messages, notifications, fetchNextCartDataPage } = useActionsData(isCurrentUser)

  const {
    isLoaded: isCartWidgetLoaded,
    loadWidget: loadCartWidget,
    popoverComponent: cartPopover,
    shouldShowCartProgress,
    cartProgress,
  } = useCartWidget({
    cart,
    fetchNextCartDataPage,
    locale,
    showCartAds,
    isCurrentUser,
  })

  const {
    isLoaded: isMessagesWidgetLoaded,
    loadWidget: loadMessagesWidget,
    popoverComponent: messagesPopover,
  } = useMessagesWidget({ messages })

  const {
    isLoaded: isNotificationsWidgetLoaded,
    loadWidget: loadNotificationsWidget,
    popoverComponent: notificationsPopover,
    markAllAsRead,
    unreadCount: notificationsUnreadCount,
  } = useNotificationsWidget({ notifications })

  const countsMap = {
    'cart-action': cart.meta.size,
    'inbox-action': messages.totalUnreadMessages,
    'notifications-action': notificationsUnreadCount,
  }

  function handleWidgetLoad(widgetId, callback) {
    if (widgetId === 'cart-action') {
      loadCartWidget(callback)
    }

    if (widgetId === 'inbox-action') {
      loadMessagesWidget(callback)
    }

    if (widgetId === 'notifications-action') {
      loadNotificationsWidget(callback)
    }
  }

  const popoversMap = {
    'cart-action': {
      hasPopover: true,
      isLoaded: isCartWidgetLoaded,
      component: cartPopover,
    },
    'inbox-action': {
      hasPopover: true,
      isLoaded: isMessagesWidgetLoaded,
      component: messagesPopover,
    },
    'notifications-action': {
      hasPopover: true,
      isLoaded: isNotificationsWidgetLoaded,
      component: notificationsPopover,
    },
  }

  const onOpenMap = {
    'notifications-action': markAllAsRead,
  }

  const bottomElementsMap = {
    'cart-action': <CartProgressBar shouldShowCartProgress={shouldShowCartProgress} cartProgress={cartProgress} />,
  }

  const visibleActions = actionsLinks.filter(({ id }) => VISIBLE_ACTION_IDS.includes(id))

  return (
    <div className="unified-nav__actions">
      <ul className="unified-nav__actions-list">
        {/* Rendered for everyone, unlike the inbox / notifications actions: a guest cannot
            start a run, so they always get the idle glyph linking to the studio. Keeping it
            ungated also keeps it in the server-rendered HTML on fast pages, so the actions
            row never reflows when pageConfig lands. */}
        <GenerationStatusAction
          createAiItem={createAiItem}
          labels={createAiGeneration}
          currentUserId={currentUserId}
        />
        {visibleActions.map(({ id, icon, isForUser }) => (
          <UserAction
            key={id}
            id={id}
            icon={ICONS_MAP[id] || icon}
            isForUser={isForUser}
            isCurrentUser={isCurrentUser}
            count={countsMap[id]}
            hasPopover={popoversMap[id]?.hasPopover}
            isPopoverLoaded={popoversMap[id]?.isLoaded}
            popoverComponent={popoversMap[id]?.component}
            bottomElement={bottomElementsMap[id] || null}
            onWidgetLoad={popoversMap[id]?.hasPopover ? handleWidgetLoad : null}
            onOpen={onOpenMap[id] || null}
            locale={locale}
          />
        ))}
      </ul>

      <UserMenu
        isCurrentUser={isCurrentUser}
        locale={locale}
        newRoyaltyUi={newRoyaltyUi}
        showFreeTrialTag={showFreeTrialTag}
        freeTrialLabel={freeTrialLabel}
      />
    </div>
  )
}

UserActions.propTypes = {
  actionsLinks: arrayOf(shape({
    id: string,
    href: string,
    icon: string,
    isForUser: bool,
  })),
  actionsConfig: shape({
    isCurrentUser: bool,
    newRoyaltyUi: bool,
    showCartAds: bool,
  }),
  locale: string,
  showFreeTrialTag: bool,
  freeTrialLabel: string,
  createAiItem: shape({
    href: string,
    title: string,
    dataTrackName: string,
  }),
  createAiGeneration: shape({
    generatingTitle: string,
    readyTitle: string,
    errorTitle: string,
    multipleGenerationsEnabled: bool,
  }),
  currentUserId: string,
}

export default memo(UserActions)
